import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import type { ComponentType } from "react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { AUTH_ENABLED } from "./config";
import { clearSignedOut, isSignedOut, markSignedOut } from "./signOut";

/** Wraps a page component, redirecting to Auth0 login if the visitor isn't
 * authenticated yet — the whole app is Staff+/PM-only, so this wraps every route.
 * A no-op when Auth0 isn't configured (see ./config.ts).
 *
 * A failed login (Auth0 redirecting back with `?error=…`, e.g. a post-login Action
 * denying the user) stops here instead. `withAuthenticationRequired` alone ignores the
 * error and redirects straight back to Auth0, which — with the identity provider's
 * session still alive — logs the same user in again, gets denied again, and loops.
 * Likewise right after signing out (see ./signOut.ts): a "Signed out" screen, so the
 * next login happens when the visitor asks for it. */
export function RequireAuth<P extends object>(Component: ComponentType<P>): ComponentType<P> {
  if (!AUTH_ENABLED) return Component;
  const Protected = withAuthenticationRequired(Component, {
    onRedirecting: () => <div className="text-(--color-text) p-8">Redirecting to login…</div>,
  });
  return function Guarded(props: P) {
    const { error, isAuthenticated, isLoading } = useAuth0();
    if (error) return <LoginError message={error.message} />;
    if (isAuthenticated) clearSignedOut();
    else if (!isLoading && isSignedOut()) return <SignedOut />;
    return <Protected {...props} />;
  };
}

/** Starts a login that asks Auth0 for credentials rather than reusing a session. An
 * upstream identity provider may still reuse its own — see IDP_SESSION_HINT. */
function useFreshLogin(): () => void {
  const { loginWithRedirect } = useAuth0();
  return () => {
    clearSignedOut();
    void loginWithRedirect({ authorizationParams: { prompt: "login" } });
  };
}

/** Shown on both the Signed out and Login failed screens: the one thing a visitor can do
 * when an upstream identity provider (Test-IdP) keeps its own session and signs the same
 * account straight back in. */
const IDP_SESSION_HINT =
  "If the same account is signed straight back in, your identity provider is still signed in on its own. " +
  "Close every private/incognito window (or clear this browser's cookies) and log in again.";

/** The screen shown right after signing out, in place of an automatic re-login. Logging
 * in again is the visitor's choice, and asks Auth0 for credentials afresh. */
function SignedOut() {
  const logIn = useFreshLogin();
  return (
    <div className="mx-auto max-w-xl p-8">
      <Card className="flex flex-col gap-3">
        <h2 className="font-medium">Signed out</h2>
        <p className="text-sm text-text/60">{IDP_SESSION_HINT}</p>
        <div>
          <Button variant="primary" onClick={logIn}>
            Log in
          </Button>
        </div>
      </Card>
    </div>
  );
}

/** The "Login failed" card shown when Auth0 redirects back with an error (e.g. a
 * post-login Action denying the account), instead of looping back to Auth0. Offers a
 * fresh login attempt, or a federated sign-out to switch accounts. */
function LoginError({ message }: { message: string }) {
  const { logout } = useAuth0();
  const logIn = useFreshLogin();
  return (
    <div className="mx-auto max-w-xl p-8">
      <Card role="alert" className="flex flex-col gap-3 border-warn-line bg-warn-bg">
        <h2 className="font-medium text-warn">Login failed</h2>
        <p className="text-sm">{message || "Auth0 didn't say why."}</p>
        <p className="text-sm text-text/60">
          The account may not be set up for this app. Sign out to use a different one. {IDP_SESSION_HINT}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button onClick={logIn}>Try again</Button>
          {/* federated: asks Auth0 to end the upstream identity provider's session too.
              Only works where that connection supports single logout — Test-IdP's
              doesn't appear to, hence the Signed out screen and IDP_SESSION_HINT. */}
          <Button
            onClick={() => {
              markSignedOut();
              void logout({ logoutParams: { returnTo: window.location.origin, federated: true } });
            }}
          >
            Sign out completely
          </Button>
        </div>
      </Card>
    </div>
  );
}
